import { useState, useEffect, useCallback, useRef } from 'react'
import { Search, User, AlertCircle, Clock, X, Zap, Flame, Leaf, History } from 'lucide-react'

const RECENT_SEARCHES_KEY = 'jobauto_recent_searches'
const MAX_RECENT = 3

function getRecentSearches(): string[] {
  try { return JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY) ?? '[]') } catch { return [] }
}

function addRecentSearch(email: string) {
  const prev = getRecentSearches().filter(e => e !== email)
  localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify([email, ...prev].slice(0, MAX_RECENT)))
}
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch'
import { formatCurrency } from '../JobAutoCalculatorHelpers'
import { cn } from '../../../shared'

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app'

interface EmployeeSubscription {
  subscriptionId: string | null
  state: string | null
  jobautoType: string | null
  jobautoCompanyId: string | null
  jobautoConfirmation: string | null
  pendingApproval: boolean
  monthlyRate: number | null
  bruttolistenpreis: number | null
  dealName: string | null
  car: { make: string | null; model: string | null; fuel: string | null } | null
  startDate: string | null
  isSwap?: boolean
  previousSubscriptionId?: string | null
  subscribedAs?: string | null
}

interface EmployeeLookupResult {
  contact: { id: string; name: string; email: string } | null
  subscriptions: EmployeeSubscription[]
}

export interface EmployeeAutoFill {
  gehaltsumwandlung: number
  bruttolistenpreis: number | null
  gwvPercentage: 0.25 | 0.5 | 1.0
  carLabel: string
  fuel: string | null
  pendingApproval: boolean
  isSwap: boolean
  previousSubscriptionId: string | null
}

interface Props {
  initialEmail?: string
  onAutoFill: (fill: EmployeeAutoFill) => void
}

function inferGwvPercentage(fuel: string | null): 0.25 | 0.5 | 1.0 {
  if (!fuel) return 1.0
  const f = fuel.toLowerCase()
  if (f.includes('electric') || f === 'bev' || f === 'elektro') return 0.25
  if (f.includes('hybrid') || f === 'phev' || f === 'plugin') return 0.5
  return 1.0
}

function FuelBadge({ fuel }: { fuel: string | null }) {
  if (!fuel) return null
  const f = fuel.toLowerCase()
  if (f.includes('electric') || f === 'bev' || f === 'elektro') {
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">
        <Zap className="h-3 w-3" /> Elektro
      </span>
    )
  }
  if (f.includes('hybrid') || f === 'phev' || f === 'plugin') {
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">
        <Leaf className="h-3 w-3" /> Hybrid
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600 font-medium">
      <Flame className="h-3 w-3" /> Verbrenner
    </span>
  )
}

export default function EmployeeSearch({ initialEmail = '', onAutoFill }: Props) {
  const [emailInput, setEmailInput] = useState(initialEmail)
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<EmployeeLookupResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [recentSearches, setRecentSearches] = useState<string[]>(getRecentSearches)

  const lookup = useCallback(async (email: string) => {
    const trimmed = email.trim().toLowerCase()
    if (!trimmed || !trimmed.includes('@')) return
    setLoading(true)
    setError(null)
    setResult(null)
    try {
      const res = await authenticatedFetch(
        `${PROXY_URL}/api/jobauto/employee-lookup?email=${encodeURIComponent(trimmed)}`
      )
      const data: EmployeeLookupResult = await res.json()
      setResult(data)
      if (data.contact) {
        addRecentSearch(trimmed)
        setRecentSearches(getRecentSearches())
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Lookup failed')
    } finally {
      setLoading(false)
    }
  }, [])

  const didInitialLookup = useRef(false)
  useEffect(() => {
    if (initialEmail && !didInitialLookup.current) {
      didInitialLookup.current = true
      void lookup(initialEmail)
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    lookup(emailInput)
  }

  const handleSelect = (sub: EmployeeSubscription) => {
    const carLabel = [sub.car?.make, sub.car?.model].filter(Boolean).join(' ') || sub.dealName || 'Fahrzeug'
    onAutoFill({
      gehaltsumwandlung: sub.monthlyRate ?? 0,
      bruttolistenpreis: sub.bruttolistenpreis,
      gwvPercentage: inferGwvPercentage(sub.car?.fuel ?? null),
      carLabel,
      fuel: sub.car?.fuel ?? null,
      pendingApproval: sub.pendingApproval,
      isSwap: sub.isSwap ?? false,
      previousSubscriptionId: sub.previousSubscriptionId ?? null,
    })
  }

  const pendingSubs = result?.subscriptions.filter(s => s.pendingApproval) ?? []

  return (
    <div className="space-y-4">
      {/* Search form */}
      <form onSubmit={handleSubmit} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <input
            type="email"
            placeholder="vorname.nachname@finn.com"
            value={emailInput}
            onChange={e => setEmailInput(e.target.value)}
            className="w-full pl-9 pr-3 py-2.5 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
          />
        </div>
        <button
          type="submit"
          disabled={loading || !emailInput.includes('@')}
          className="px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? 'Suche…' : 'Suchen'}
        </button>
      </form>

      {/* Recent searches */}
      {recentSearches.length > 0 && !result && !loading && (
        <div className="flex items-center gap-2 flex-wrap">
          <History className="h-3.5 w-3.5 text-zinc-400 shrink-0" />
          {recentSearches.map(email => (
            <button
              key={email}
              onClick={() => { setEmailInput(email); lookup(email) }}
              className="text-xs px-2.5 py-1 rounded-full bg-zinc-100 text-zinc-600 hover:bg-blue-50 hover:text-blue-700 transition-colors"
            >
              {email}
            </button>
          ))}
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {result && !result.contact && (
        <div className="text-sm text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 space-y-1">
          <div className="flex items-center gap-2">
            <X className="h-4 w-4 shrink-0" />
            <span>Kein HubSpot-Kontakt für diese E-Mail gefunden.</span>
          </div>
          <p className="text-xs text-zinc-400 pl-6">
            Mögliche Ursachen: Mitarbeiter ist mit einer anderen E-Mail (z.B. privat) bei FINN registriert, oder existiert noch nicht als HubSpot-Kontakt.
          </p>
        </div>
      )}

      {result?.contact && (
        <div className="space-y-4">
          {/* Contact pill */}
          <div className="flex items-center gap-2 text-sm text-zinc-600">
            <User className="h-4 w-4 text-zinc-400" />
            <span className="font-medium">{result.contact.name}</span>
            <span className="text-zinc-300">·</span>
            <span className="text-zinc-400">{result.contact.email}</span>
            <button
              onClick={() => { setResult(null); setEmailInput('') }}
              className="ml-auto flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-700 transition-colors"
            >
              <X className="h-3.5 w-3.5" /> Zurücksetzen
            </button>
          </div>

          {result.subscriptions.length === 0 && (
            <p className="text-sm text-zinc-500">Keine JobAuto-Subscriptions gefunden.</p>
          )}

          {/* Pending */}
          {pendingSubs.length > 0 && (
            <div className="space-y-3">
              <div className="text-xs font-semibold text-orange-700 uppercase tracking-wide flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                Ausstehende Arbeitgeberzustimmung ({pendingSubs.length})
              </div>
              {pendingSubs.map(sub => (
                <SubscriptionCard key={sub.subscriptionId} sub={sub} onSelect={() => handleSelect(sub)} />
              ))}
            </div>
          )}

          {pendingSubs.length === 0 && result.subscriptions.length > 0 && (
            <p className="text-sm text-zinc-500">
              Keine Zustimmung ausstehend — {result.subscriptions.length === 1 ? 'die Subscription ist' : `alle ${result.subscriptions.length} Subscriptions sind`} bereits bestätigt.
            </p>
          )}
          {pendingSubs.length === 0 && result.subscriptions.length === 0 && (
            <p className="text-sm text-zinc-500">
              Keine aktiven JobAuto-Subscriptions gefunden.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

function SubscriptionCard({
  sub,
  onSelect,
}: {
  sub: EmployeeSubscription
  onSelect: () => void
}) {
  const make = sub.car?.make ?? null
  const model = sub.car?.model ?? null
  const carName = [make, model].filter(Boolean).join(' ') || sub.dealName || 'Fahrzeug'
  const isPending = sub.pendingApproval

  return (
    <div
      className={cn(
        'rounded-xl border p-4 transition-all cursor-pointer hover:shadow-md group',
        isPending
          ? 'border-orange-200 bg-orange-50 hover:border-orange-400'
          : 'border-zinc-200 bg-white hover:border-blue-400'
      )}
      onClick={onSelect}
    >
      <div className="flex items-start justify-between gap-3">
        {/* Car info */}
        <div className="space-y-1.5">
          {make && (
            <div className="text-xs font-semibold text-zinc-400 uppercase tracking-widest">{make}</div>
          )}
          <div className="text-xl font-bold text-zinc-900">{model || carName}</div>
          <div className="flex items-center gap-2 flex-wrap">
            <FuelBadge fuel={sub.car?.fuel ?? null} />
            {isPending && (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">
                <Clock className="h-3 w-3" /> Zustimmung ausstehend
              </span>
            )}
          </div>
        </div>

        {/* Rate + CTA */}
        <div className="text-right shrink-0 space-y-2">
          {sub.monthlyRate != null && (
            <div>
              <div className="text-2xl font-bold text-zinc-900">{formatCurrency(sub.monthlyRate)}</div>
              <div className="text-xs text-zinc-400">/ Monat Gehaltsumwandlung</div>
            </div>
          )}
          <div className={cn(
            'text-xs px-3 py-1.5 rounded-lg font-medium transition-colors',
            isPending
              ? 'bg-orange-500 text-white group-hover:bg-orange-600'
              : 'bg-blue-600 text-white group-hover:bg-blue-700'
          )}>
            Auswählen →
          </div>
        </div>
      </div>

      {sub.bruttolistenpreis != null && (
        <div className="mt-3 pt-3 border-t border-zinc-200/60 text-xs text-zinc-400">
          Bruttolistenpreis {formatCurrency(sub.bruttolistenpreis)}
        </div>
      )}
    </div>
  )
}
